/**
 * [Insider+] Outer-portal / Discourse entry detection + landing-page seeding.
 */

import TagManager from 'react-gtm-module';
import { INSIDER_PLUS_PAGE } from '../../constants/pages';
import {
  INSIDER_PLUS_ENTRY_SOURCE,
  LANDING_PAGE,
} from '../../constants/sessionStorage';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from './sessionStorageHelper';
import { getStoredInsiderPlusPath } from './insiderPlusPathHelper';

const INSIDER_PLUS_CLICKED_PARAM = 'InsiderPlusClicked';
const INSIDER_PLUS_PATH_PARAM = 'InsiderPlusPath';
export const OUTER_PORTAL_SOURCE = 'outer_portal';
export const DISCOURSE_SOURCE = 'discourse';

// Detects the ?InsiderPlusClicked=yes deep-link from the marketing site
// (goldsilver.com) and seeds the existing post-login landing-page flow so
// the user ends up on /insider-plus after login + 2FA. Mirrors the BUY
// default in landingPageHelper.
export const handleInsiderPlusEntry = () => {
  if (typeof window === 'undefined') return null;

  const url = new URL(window.location.href);
  const flag = url.searchParams.get(INSIDER_PLUS_CLICKED_PARAM);
  if (flag !== 'yes') return null;

  storeInSessionStorage(LANDING_PAGE, INSIDER_PLUS_PAGE);
  storeInSessionStorage(INSIDER_PLUS_ENTRY_SOURCE, OUTER_PORTAL_SOURCE);

  // Rewrite to /insider-plus so PrivateRoute resolves against the right
  // path (unauth: stores it as LANDING_PAGE; auth: renders directly).
  url.pathname = INSIDER_PLUS_PAGE;
  url.searchParams.delete(INSIDER_PLUS_CLICKED_PARAM);
  window.history.replaceState({}, '', url.toString());

  TagManager.dataLayer({
    dataLayer: {
      event: 'insider_plus_entry',
      source: OUTER_PORTAL_SOURCE,
    },
  });

  return OUTER_PORTAL_SOURCE;
};

// sessionStorage doesn't cross origins. A visitor who arrives via an
// Insider+ link and then clicks "Sign up" (SignUpUrl, or a broker's
// PortalUrl-hosted registration) may leave this origin entirely — losing
// LANDING_PAGE / INSIDER_PLUS_LAST_PATH before they ever create an account.
// Re-encoding the current entry as a query param on that outbound link lets
// it survive the hop IF the destination eventually links back here with the
// param intact — same contract as the inbound handlers above, just mirrored
// outbound. Returns null outside an Insider+ journey (nothing to carry).
export const getInsiderPlusOutboundParams = () => {
  if (typeof window === 'undefined') return null;
  if (
    retrieveFromSessionStorage(INSIDER_PLUS_ENTRY_SOURCE) !==
    OUTER_PORTAL_SOURCE
  ) {
    return null;
  }

  const deepPath = getStoredInsiderPlusPath();
  return deepPath
    ? { [INSIDER_PLUS_PATH_PARAM]: deepPath }
    : { [INSIDER_PLUS_CLICKED_PARAM]: 'yes' };
};

// Appends getInsiderPlusOutboundParams() onto `url`, preserving any params
// it already has. Returns `url` unchanged if there's nothing to carry or it
// isn't a parseable URL (e.g. an empty/misconfigured broker setting).
export const withInsiderPlusOutboundContext = (url) => {
  if (!url || typeof window === 'undefined') return url;
  const params = getInsiderPlusOutboundParams();
  if (!params) return url;

  try {
    const target = new URL(url, window.location.origin);
    Object.entries(params).forEach(([key, value]) => {
      target.searchParams.set(key, value);
    });
    return target.toString();
  } catch {
    return url;
  }
};
